import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLogon } from '@core/Utils/hooks'
import {
  getInAppMarketingActiveNotifications,
  dismissNotification,
  recordNotificationInteraction,
} from './api'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { InAppNotification } from '@mobi/api-types/inAppMarketing'

const ONE_HOUR = 60 * 60 * 1000

/**
 * Custom hook for managing active in-app marketing notifications.
 *
 * @remarks
 * This hook fetches active marketing notifications for the logged-in user and provides
 * methods to interact with and dismiss notifications. It automatically manages the cache
 * by removing notifications from the query data when they are dismissed or interacted with.
 *
 * The hook will only fetch notifications when an account number is available (user is logged in).
 * Notifications are cached for one hour to reduce unnecessary API calls.
 *
 * @returns
 * - `activeNotification`: The first active notification from the fetched list, or undefined if none exist
 * - `dismissNotification`: A function to dismiss the current active notification
 * - `recordNotificationInteraction`: A function to record user interaction with a notification
 *
 */
export const useActiveMarketingNotification = () => {
  const { accountNumber } = useLogon()
  const queryClient = useQueryClient()

  const notifications = useQuery({
    queryKey: [queryKeys.inAppMarketingNotifications, accountNumber],
    queryFn: getInAppMarketingActiveNotifications,
    enabled: !!accountNumber,
    staleTime: ONE_HOUR,
    retry: 2,
    retryDelay: 3000,
    // Set inital data placeholder data and mark stale
    initialData: [],
    initialDataUpdatedAt: 0,
  })

  const activeNotification = notifications.data[0]

  const recordNotificationInteractionMutation = useMutation({
    mutationFn: async (buttonIndex: number) => {
      if (!activeNotification) return
      queryClient.setQueryData(
        [queryKeys.inAppMarketingNotifications, accountNumber],
        (oldData: InAppNotification[]) => {
          return oldData.filter(notification => notification.id !== activeNotification?.id)
        }
      )

      recordNotificationInteraction(activeNotification.id, buttonIndex)
    },
  })

  const dismissNotificationMutation = useMutation({
    mutationFn: async () => {
      if (!activeNotification) return
      queryClient.setQueryData(
        [queryKeys.inAppMarketingNotifications, accountNumber],
        (oldData: InAppNotification[]) => {
          return oldData.filter(notification => notification.id !== activeNotification?.id)
        }
      )

      dismissNotification(activeNotification.id)
    },
  })

  return {
    activeNotification,
    dismissNotification: dismissNotificationMutation.mutate,
    recordNotificationInteraction: recordNotificationInteractionMutation.mutate,
  }
}
